/**
 * PurchaseOrderForm — Purchase Order create + edit form (D-034 §3.1).
 *
 * Redesigned 2026-05-05 (CR-2026-FE-PO-FORM-REDESIGN, Class P) onto the
 * PaperForm component library. Source-of-design: D-034 §3.1 (AISC-loaded
 * fabricator PO with material-traceability clauses) at sfm-drawing-feature/docs/qms/_source/forms-po-templates/
 * F1.6.0PurchaseOrder.xlsx, sheet "P.O. Model 1".
 *
 * Layout faithful to the D-034 §3.1 paper PO:
 *   - Header strip: company / "PURCHASE ORDER" title / P.O. NUMBER
 *   - 2-column "To (Vendor)" / "Ship To" grid
 *   - 5-cell PO Date / Requisitioner / Shipped Via / F.O.B. Point / Terms strip
 *   - Notes (free-text) — line items live on a separate page after save
 *   - Lifecycle hint pointing to /purchaseorderitem/:id for line entry
 *
 * The 9-category AISC clause block from D-034 §3.1 lives in the PRINT
 * layout (Details.jsx commit 0909b64) — not in the data-capture form. This
 * separation matches the D-034 §3.1 source-of-design where the clauses are
 * printed terms, not vendor input fields.
 */

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import {
  Stack, Typography, Button, Alert, Box, Grid, TextField, MenuItem,
  Breadcrumbs, Link as MuiLink, IconButton, Divider, Paper,
} from "@mui/material";
import {
  HomeRounded, ArrowBackRounded, ReceiptLongOutlined,
} from "@mui/icons-material";

import ApiCall from "../../utils/apicall";
import { validatePurchaseOrderForm } from "../../utils/PurchaseValidation";
import {
  PaperFormCard, PaperFormHeader, PaperFormSection, PaperFormFooter,
  FieldLabel,
} from "../../components/PaperForm";

const PO_STATUS_OPTIONS = [
  "DRAFT", "ISSUED", "ACKNOWLEDGED", "PARTIAL_RECEIPT", "COMPLETE", "CANCELLED",
];

function PurchaseOrderForm() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formErrors, setFormErrors] = useState({});
  const [topError, setTopError] = useState(null);
  const [formData, setFormData] = useState({
    delivery_date: "",
    confirm_with: "",
    vendor_name: "",
    userId: "",
    order_date: "",
    placed_via: "",
    po_number: "",
    ship_via: "",
    order_by: "",
    ship_to: "",
    address: "",
    phone: "",
    email: "",
    term: "",
    fax: "",
    status: "DRAFT",
    company_name: "",
    requisitioner: "",
    fob_point: "",
    notes: "",
  });

  const [companies, setCompanies] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [users, setUsers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const fetchPurchaseOrderData = async () => {
      try {
        const [responseCompanies, responseVendors, responseUser] = await Promise.all([
          ApiCall.get("/company").catch(() => ({ data: { data: { companies: [] } } })),
          ApiCall.get("/vendor").catch(() => ({ data: { data: { vendors: [] } } })),
          ApiCall.get("/user").catch(() => ({ data: { data: { users: [] } } })),
        ]);
        setCompanies(responseCompanies.data?.data?.companies || []);
        setVendors(responseVendors.data?.data?.vendors || []);
        setUsers(responseUser.data?.data?.users || []);
        if (id) {
          const r = await ApiCall.get(`/purchaseorder/${id}`);
          const po = r.data?.data?.purchaseOrder || {};
          setFormData((prev) => ({ ...prev, ...po }));
          setIsUpdateMode(true);
        }
      } catch (err) {
        // Errors per-call are caught above; reaching here means an unexpected error
        console.error("PO form load error:", err);
        setTopError(err?.response?.data?.message || err?.message || "Failed to load PO data.");
      }
    };
    if (!isFormInitialized) {
      setIsFormInitialized(true);
      fetchPurchaseOrderData();
    }
  }, [id, isFormInitialized]);

  const handleSubmit = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    setIsSubmitting(true);
    setTopError(null);
    if (!isFormInitialized) {
      setIsSubmitting(false);
      return;
    }
    const values = validatePurchaseOrderForm(formData);
    setFormErrors(values.errors);
    if (Object.keys(values.errors).length > 0) {
      setTopError(`Please fix ${Object.keys(values.errors).length} field(s) flagged below.`);
      setIsSubmitting(false);
      return;
    }
    try {
      let response;
      if (id) {
        response = await ApiCall.put(`/purchaseorder/${id}`, formData);
        if (response.status === 200 || response.status === 201) {
          toast("Purchase Order updated successfully.");
          navigate("/purchaseorder");
        }
      } else {
        response = await ApiCall.post("/purchaseorder", formData);
        if (response.status === 200 || response.status === 201) {
          toast("Purchase Order created — add line items next.");
          navigate(`/purchaseorderitem/${response.data.data.purchaseOrder.id}`);
        }
      }
    } catch (err) {
      console.error("PO submit error:", err);
      setTopError(err?.response?.data?.message || err?.message || "Submit failed. Please retry.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const poDate = useMemo(() => {
    if (formData.order_date) return new Date(formData.order_date).toLocaleDateString();
    return new Date().toLocaleDateString();
  }, [formData.order_date]);

  const status = formData.status || "DRAFT";
  const poNumber = formData.po_number || (isUpdateMode ? `#${id}` : "Assigned automatically");

  return (
    <>
      <Helmet>
        <title>{isUpdateMode ? "Edit Purchase Order" : "New Purchase Order"} | SFM</title>
      </Helmet>

      <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
        <Box sx={{ maxWidth: "lg", mx: "auto", pt: 3, px: { xs: 2, md: 3 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Breadcrumbs>
              <MuiLink href="/" color="inherit" underline="hover">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <HomeRounded fontSize="small" />
                  <span>Home</span>
                </Stack>
              </MuiLink>
              <MuiLink href="/purchaseorder" color="inherit" underline="hover">Purchase Orders</MuiLink>
              <Typography color="text.primary">
                {isUpdateMode ? `Edit #${id}` : "New"}
              </Typography>
            </Breadcrumbs>
            <IconButton onClick={() => navigate("/purchaseorder")} aria-label="Back to PO list">
              <ArrowBackRounded />
            </IconButton>
          </Stack>

          {topError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTopError(null)}>{topError}</Alert>}
        </Box>

        <PaperFormCard maxWidth="lg">
          <PaperFormHeader
            formId="SFM · Purchase Order"
            title="Purchase Order"
            subtitle="AISC §1.5.0 — procurement and material traceability"
            status={status}
            number={poNumber}
            numberLabel="P.O. No."
            date={poDate}
            icon="receipt_long"
          />

          <Box component="form" onSubmit={handleSubmit}>
            {/* TWO-COLUMN VENDOR + SHIP-TO GRID — matches D-034 §3.1 paper layout */}
            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <PaperFormSection label="To (Vendor)" bottomPadding={0}>
                  <Box>
                    <FieldLabel required>Vendor</FieldLabel>
                    <TextField
                      select fullWidth size="small"
                      name="vendor_name"
                      value={formData.vendor_name || ""}
                      onChange={handleChange}
                      error={formErrors.vendor_id !== undefined || formErrors.vendor_name !== undefined}
                      helperText={formErrors.vendor_id || formErrors.vendor_name}
                    >
                      <MenuItem value=""><em>Select vendor…</em></MenuItem>
                      {vendors.map((v) => (
                        <MenuItem key={v.id} value={v.id}>{v.vendor_name}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box>
                    <FieldLabel>Confirm With</FieldLabel>
                    <TextField
                      fullWidth size="small"
                      name="confirm_with"
                      placeholder="Vendor contact (e.g., Joe Owner)"
                      value={formData.confirm_with || ""}
                      onChange={handleChange}
                      error={formErrors.confirm_with !== undefined}
                      helperText={formErrors.confirm_with}
                    />
                  </Box>
                  <Box>
                    <FieldLabel>Address</FieldLabel>
                    <TextField
                      fullWidth size="small" multiline rows={2}
                      name="address"
                      placeholder="Street, City, State ZIP"
                      value={formData.address || ""}
                      onChange={handleChange}
                      error={formErrors.address !== undefined}
                      helperText={formErrors.address}
                    />
                  </Box>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <FieldLabel>Phone</FieldLabel>
                      <TextField
                        fullWidth size="small"
                        name="phone"
                        placeholder="(123) 456-7890"
                        value={formData.phone || ""}
                        onChange={handleChange}
                        error={formErrors.phone !== undefined}
                        helperText={formErrors.phone}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <FieldLabel>Fax</FieldLabel>
                      <TextField
                        fullWidth size="small"
                        name="fax"
                        placeholder="optional"
                        value={formData.fax || ""}
                        onChange={handleChange}
                        error={formErrors.fax !== undefined}
                        helperText={formErrors.fax}
                      />
                    </Box>
                  </Stack>
                  <Box>
                    <FieldLabel>Email</FieldLabel>
                    <TextField
                      fullWidth size="small" type="email"
                      name="email"
                      placeholder="vendor@example.com"
                      value={formData.email || ""}
                      onChange={handleChange}
                      error={formErrors.email !== undefined}
                      helperText={formErrors.email}
                    />
                  </Box>
                </PaperFormSection>
              </Grid>

              <Grid item xs={12} md={6}>
                <PaperFormSection label="Ship To & Issuer" bottomPadding={0}>
                  <Box>
                    <FieldLabel required>Issuing Company</FieldLabel>
                    <TextField
                      select fullWidth size="small"
                      name="company_name"
                      value={formData.company_name || ""}
                      onChange={handleChange}
                      error={formErrors.company_id !== undefined || formErrors.company_name !== undefined}
                      helperText={formErrors.company_id || formErrors.company_name}
                    >
                      <MenuItem value=""><em>Select company…</em></MenuItem>
                      {companies.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box>
                    <FieldLabel>Ship To</FieldLabel>
                    <TextField
                      fullWidth size="small" multiline rows={2}
                      name="ship_to"
                      placeholder="Receiving location address"
                      value={formData.ship_to || ""}
                      onChange={handleChange}
                      error={formErrors.ship_to !== undefined}
                      helperText={formErrors.ship_to}
                    />
                  </Box>
                  <Box>
                    <FieldLabel hint="A3 staff who owns this PO">Assigned To</FieldLabel>
                    <TextField
                      select fullWidth size="small"
                      name="userId"
                      value={formData.userId || ""}
                      onChange={handleChange}
                      error={formErrors.userId !== undefined}
                      helperText={formErrors.userId}
                    >
                      <MenuItem value=""><em>Unassigned</em></MenuItem>
                      {users.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.firstName} {u.lastName || ""}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box>
                    <FieldLabel>Status</FieldLabel>
                    <TextField
                      select fullWidth size="small"
                      name="status"
                      value={formData.status || "DRAFT"}
                      onChange={handleChange}
                    >
                      {PO_STATUS_OPTIONS.map((s) => (
                        <MenuItem key={s} value={s}>{s.replace(/_/g, " ")}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </PaperFormSection>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 4 }} />

            {/* 5-CELL PO METADATA STRIP — matches D-034 §3.1 paper row 22 */}
            <PaperFormSection
              label="P.O. Metadata"
              hint="dates, requisitioner, ship-via, F.O.B., terms"
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FieldLabel>P.O. Date</FieldLabel>
                  <TextField
                    fullWidth size="small" type="date"
                    name="order_date"
                    InputLabelProps={{ shrink: true }}
                    value={(formData.order_date || "").toString().split("T")[0]}
                    onChange={handleChange}
                    error={formErrors.order_date !== undefined}
                    helperText={formErrors.order_date}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FieldLabel>Delivery Date</FieldLabel>
                  <TextField
                    fullWidth size="small" type="date"
                    name="delivery_date"
                    InputLabelProps={{ shrink: true }}
                    value={(formData.delivery_date || "").toString().split("T")[0]}
                    onChange={handleChange}
                    error={formErrors.delivery_date !== undefined}
                    helperText={formErrors.delivery_date}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FieldLabel>P.O. Number</FieldLabel>
                  <TextField
                    fullWidth size="small"
                    name="po_number"
                    placeholder="e.g., 2026-PO-101"
                    value={formData.po_number || ""}
                    onChange={handleChange}
                    error={formErrors.po_number !== undefined}
                    helperText={formErrors.po_number}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <FieldLabel>Requisitioner</FieldLabel>
                  <TextField
                    fullWidth size="small"
                    name="requisitioner"
                    placeholder="A3 staff requesting"
                    value={formData.requisitioner || ""}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FieldLabel>Order By</FieldLabel>
                  <TextField
                    fullWidth size="small"
                    name="order_by"
                    placeholder="Approver name"
                    value={formData.order_by || ""}
                    onChange={handleChange}
                    error={formErrors.order_by !== undefined}
                    helperText={formErrors.order_by}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FieldLabel>Placed Via</FieldLabel>
                  <TextField
                    fullWidth size="small"
                    name="placed_via"
                    placeholder="e.g., email, phone, web"
                    value={formData.placed_via || ""}
                    onChange={handleChange}
                    error={formErrors.placed_via !== undefined}
                    helperText={formErrors.placed_via}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <FieldLabel>Shipped Via</FieldLabel>
                  <TextField
                    fullWidth size="small"
                    name="ship_via"
                    placeholder="e.g., common carrier, will-call"
                    value={formData.ship_via || ""}
                    onChange={handleChange}
                    error={formErrors.ship_via !== undefined}
                    helperText={formErrors.ship_via}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FieldLabel hint="F.O.B. point">F.O.B. Point</FieldLabel>
                  <TextField
                    fullWidth size="small"
                    name="fob_point"
                    placeholder="e.g., Origin / Destination"
                    value={formData.fob_point || ""}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FieldLabel hint="payment terms">Terms</FieldLabel>
                  <TextField
                    fullWidth size="small"
                    name="term"
                    placeholder="e.g., Net 30, Due on receipt"
                    value={formData.term || ""}
                    onChange={handleChange}
                    error={formErrors.term !== undefined}
                    helperText={formErrors.term}
                  />
                </Grid>
              </Grid>
            </PaperFormSection>

            {/* NOTES */}
            <PaperFormSection
              label="Notes"
              hint="optional · context, reference numbers, special handling"
            >
              <TextField
                fullWidth multiline rows={3}
                name="notes"
                placeholder="e.g., Per RFI 2026-014 — switch to A572 Gr. 50 for these line items."
                value={formData.notes || ""}
                onChange={handleChange}
              />
            </PaperFormSection>

            {/* LIFECYCLE HINT */}
            <Alert severity="info" sx={{ mb: 4 }}>
              {isUpdateMode ? (
                <>
                  Line items are managed on a separate page —
                  <MuiLink href={`/purchaseorderitem/${id}`} sx={{ ml: 0.5 }}>open the items editor</MuiLink>.
                  AISC §1.5.0 §2.6 material-traceability clauses (PO/PMK/heat marking, MTRs, paint COCs,
                  weld consumables, BOL linkage, fastener procurement) print on the PO output via the print
                  layout — not captured here.
                </>
              ) : (
                <>
                  After creation, you'll be redirected to the line items editor to add quantities, descriptions,
                  and unit prices. AISC clause block (per D-034 §3.1) prints automatically on Print PO output for
                  structural-fab POs.
                </>
              )}
            </Alert>

            <PaperFormFooter
              citation="AISC §1.5.0 — order review and material traceability"
            />

            {/* ACTION BAR */}
            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 4 }}>
              <Button
                variant="text"
                onClick={() => navigate("/purchaseorder")}
                disabled={isSubmitting}
                sx={{ color: "text.secondary" }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<ReceiptLongOutlined />}
                disabled={isSubmitting}
                sx={{
                  px: 3,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  letterSpacing: "0.01em",
                }}
              >
                {isSubmitting ? "Submitting…" : (isUpdateMode ? "Save changes" : "Create Purchase Order")}
              </Button>
            </Stack>
          </Box>
        </PaperFormCard>
      </Box>
    </>
  );
}

export default PurchaseOrderForm;
